import { useState, type ReactNode } from "react";
import { Box, Button, ButtonBase, IconButton, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileDropDialog from "../common/FileDropDialog";
import { EvidenceAttachments } from "../../hooks/useEvidenceAttachments";
import { useAttachmentUrl } from "../../hooks/useAttachmentUrl";
import {
  EVIDENCE_APPENDING_SLOTS,
  EVIDENCE_SLOT_LABELS,
  EvidenceAttachmentRef,
  EvidenceSlot,
  IMAGE_UPLOAD_ACCEPT,
  imageUploadError,
} from "../../types/evidence";

const TILE = 44;
// Two lines of the longest slot label at the caption's 10px.
const CAPTIONED_WIDTH = 96;

function TileFrame({ dashed, grey, children }: { dashed?: boolean; grey?: boolean; children?: ReactNode }) {
  return (
    <Box
      sx={{
        width: TILE,
        height: TILE,
        flexShrink: 0,
        position: "relative",
        overflow: "hidden",
        borderRadius: 1,
        border: "1px",
        borderStyle: dashed ? "dashed" : "solid",
        borderColor: "divider",
        bgcolor: grey ? "grey.200" : "background.default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& .tile-remove, & .tile-replace": { opacity: 0 },
        "&:hover .tile-remove, &:focus-within .tile-remove, &:hover .tile-replace, &:focus-within .tile-replace": { opacity: 1 },
        "@media (hover: none)": { "& .tile-remove, & .tile-replace": { opacity: 1 } },
      }}
    >
      {children}
    </Box>
  );
}

// Shown on hover or focus over a tile that opens the replace dialog.
function ReplaceOverlay() {
  return (
    <Typography
      className="tile-replace"
      variant="caption"
      sx={{ position: "absolute", left: 0, right: 0, bottom: 0, fontSize: 10, lineHeight: 1.6, textAlign: "center", bgcolor: "background.paper", pointerEvents: "none" }}
    >
      Replace
    </Typography>
  );
}

function FileTile({ draftId, attachment, attachments, disabled, onReplace }: {
  draftId: number;
  attachment: EvidenceAttachmentRef;
  attachments: EvidenceAttachments;
  disabled?: boolean;
  /** A single-file slot: clicking the tile swaps the file. */
  onReplace?: { label: string; disabled: boolean; open: () => void };
}) {
  const src = useAttachmentUrl(draftId, attachment.storageKey);
  return (
    <TileFrame dashed={!src}>
      {src && (
        <Box
          component="img"
          src={src}
          alt={attachment.fileName}
          title={attachment.fileName}
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}
      {onReplace && (
        <ButtonBase
          aria-label={`Replace ${onReplace.label}`}
          disabled={onReplace.disabled || attachments.busy(attachment.storageKey)}
          onClick={onReplace.open}
          sx={{ position: "absolute", inset: 0 }}
        >
          <ReplaceOverlay />
        </ButtonBase>
      )}
      <IconButton
        className="tile-remove"
        size="small"
        aria-label={`Remove ${attachment.fileName}`}
        disabled={disabled || attachments.disabled || attachments.busy(attachment.storageKey)}
        onClick={() => attachments.remove(attachment.storageKey)}
        sx={{ position: "absolute", top: 0, right: 0, p: 0.25, bgcolor: "background.paper", "&:hover": { bgcolor: "background.paper" } }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </TileFrame>
  );
}

export default function AttachmentTiles({ draftId, slot, attachments, generated, disabled, caption }: {
  draftId: number;
  slot: EvidenceSlot;
  attachments: EvidenceAttachments;
  /** The document already prints a rendering of this slot: with no file, the generated tile opens the replace dialog. */
  generated?: boolean;
  disabled?: boolean;
  /** Name the slot under its tiles; a string names it that instead. */
  caption?: boolean | string;
}) {
  const [dropOpen, setDropOpen] = useState(false);

  if (attachments.unknown) return null;

  const label = EVIDENCE_SLOT_LABELS[slot];
  const captionText = typeof caption === "string" ? caption : label;
  const files = attachments.attachments.filter((a) => a.slot === slot);
  const appends = EVIDENCE_APPENDING_SLOTS.includes(slot);
  const addDisabled = disabled || attachments.disabled || attachments.busy(slot);
  const showGenerated = generated && files.length === 0;
  const replace = { label, disabled: addDisabled, open: () => setDropOpen(true) };

  return (
    <Box sx={{ display: "inline-flex", flexDirection: "column", width: caption ? CAPTIONED_WIDTH : undefined, maxWidth: "100%" }}>
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.75} alignItems="center">
        {files.map((a) => (
          <FileTile
            key={a.storageKey}
            draftId={draftId}
            attachment={a}
            attachments={attachments}
            disabled={disabled}
            onReplace={appends ? undefined : replace}
          />
        ))}
        {showGenerated && (
          <TileFrame grey>
            <ButtonBase
              aria-label={`Replace ${label}`}
              title="Generated from the order data"
              disabled={addDisabled}
              onClick={replace.open}
              sx={{ position: "absolute", inset: 0 }}
            >
              <DescriptionOutlinedIcon aria-hidden={false} role="img" aria-label="Generated exhibit" sx={{ fontSize: 20, color: "text.secondary" }} />
              <ReplaceOverlay />
            </ButtonBase>
          </TileFrame>
        )}
        {!showGenerated && (appends || files.length === 0) && (
          <Button
            aria-label={`Add ${label} screenshot`}
            disabled={addDisabled}
            onClick={() => setDropOpen(true)}
            sx={{ minWidth: 0, width: TILE, height: TILE, p: 0, borderRadius: 1, border: "1px dashed", borderColor: "divider" }}
          >
            <AddIcon fontSize="small" />
          </Button>
        )}
      </Stack>
      {caption && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: 10, mt: 0.25 }}>
          {captionText}
        </Typography>
      )}
      {files.map((a) => attachments.errors[a.storageKey] && (
        <Typography key={a.storageKey} variant="caption" color="error" sx={{ display: "block" }}>
          {attachments.errors[a.storageKey]}
        </Typography>
      ))}
      {dropOpen && (
        <FileDropDialog
          title={label}
          description={showGenerated ? "This replaces the generated screenshot in the package." : undefined}
          accept={IMAGE_UPLOAD_ACCEPT}
          hint="PNG or JPEG, up to 5 MB"
          multiple={appends}
          validate={(file) => imageUploadError(file, "Attachment")}
          upload={(file) => attachments.upload(slot, slot, file)}
          onClose={() => setDropOpen(false)}
        />
      )}
    </Box>
  );
}
